"use client";

import { createLogger } from "@/lib/logger";
import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { ONBOARDING_STEP_IDS, resolveInitialOnboardingState } from "./experience";
import { OnboardingState, QuizAnswers } from "./types";

const logger = createLogger("onboarding");

interface OnboardingContextType {
  state: OnboardingState;
  answers: QuizAnswers;
  currentStep: number;
  totalSteps: number;
  progress: number;
  isHydrated: boolean;
  setAnswer: <K extends keyof QuizAnswers>(key: K, value: QuizAnswers[K]) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (step: number) => void;
  setTotalSteps: (total: number) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const STORAGE_KEY = "chickytutor_onboarding_state";

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const [state, setState] = useState<OnboardingState>({
    currentStep: 0,
    totalSteps: ONBOARDING_STEP_IDS.length,
    answers: {},
    startTime: Date.now(),
  });

  // Load state from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        setState(resolveInitialOnboardingState(saved, window.location.search));
      } catch (e) {
        logger.error("Error loading onboarding state", {
          error: e instanceof Error ? e.message : String(e),
        });
      }
    }
    setMounted(true);
  }, []);

  // Save state to localStorage on change
  useEffect(() => {
    if (mounted && typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        logger.error("Error saving onboarding state", {
          error: e instanceof Error ? e.message : String(e),
        });
      }
    }
  }, [state, mounted]);

  const setAnswer = useCallback(<K extends keyof QuizAnswers>(key: K, value: QuizAnswers[K]) => {
    setState((prev) => ({
      ...prev,
      answers: { ...prev.answers, [key]: value },
    }));
  }, []);

  const goToNextStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, prev.totalSteps - 1),
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const goToPreviousStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0),
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const goToStep = useCallback((step: number) => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(0, Math.min(step, prev.totalSteps - 1)),
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const setTotalSteps = useCallback((total: number) => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep, Math.max(0, total - 1)),
      totalSteps: total,
    }));
  }, []);

  const progress = ((state.currentStep + 1) / state.totalSteps) * 100;

  const value: OnboardingContextType = {
    state,
    answers: state.answers,
    currentStep: state.currentStep,
    totalSteps: state.totalSteps,
    progress,
    isHydrated: mounted,
    setAnswer,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    setTotalSteps,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
}
